/**
 * Design presets — the effective rendered identity of every selectable
 * design in the studio.
 *
 * Why this file exists (2026-11-03 design audit): applyBankTemplate used
 * to set a per-bank texture and then a final catch-all else-branch
 * overwrote it with guilloche_bg for almost every template, and its font
 * mapping only honoured Courier/Helvetica/Arial spec strings. The result
 * was that 43 of the 48 bank templates rendered with one of only two
 * (texture, font, paper) combinations — designs differed by colour and
 * printed name only. The check-style buttons likewise changed nothing
 * but the style id, so Personal / Business / Counter / Money Order
 * rendered the identical check.
 *
 * These maps are the single authoritative source: one entry per design,
 * built ONLY from real studio parameters (texture / font / paper ids
 * from CheckBuilder's TEXTURES, FONTS and PAPER_COLORS lists, border
 * styles from BorderlineEngine). A unit test
 * (designPresets.test.ts) enforces coverage, validity, and that no two
 * designs collapse to the same effective combination.
 */

export interface TemplateDesign {
    texture: string;
    font: string;
    paper: string;
    /** Texture opacity override (treasury / ultra / karen keep theirs). */
    opacity?: number;
}

/** Effective design for every bank template id and premium template id. */
export const TEMPLATE_DESIGN: Record<string, TemplateDesign> = {
    // ---- Major banks -------------------------------------------------
    BOFA: { texture: 'cloud_marble', font: 'helvetica', paper: 'white' },
    BOFA_CLASSIC: { texture: 'cloud_marble', font: 'courier', paper: 'light_blue' },
    BOFA_BUSINESS_V2: { texture: 'blue_wave', font: 'arial', paper: 'white' },
    US_TREASURY_OFFICIAL: { texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' },
    CHASE: { texture: 'security', font: 'helvetica', paper: 'white' },
    CHASE_PRIVATE: { texture: 'security', font: 'garamond', paper: 'cream' },
    WELLS: { texture: 'linen', font: 'helvetica', paper: 'white' },
    CITI: { texture: 'geometric_diamond', font: 'helvetica', paper: 'white' },
    USAA: { texture: 'linen', font: 'helvetica', paper: 'white' },
    // GreenFi EXACT rebuild (2026-11-04): the specimen paper is
    // SMOOTH and near-white — 'plain' texture on the palest green
    // paper. The old entry's linen weave (a visible grid + fibres
    // over mint) was the rejected first build ("too greenish...
    // a little bit white background with a touch of light green").
    GREENFI: { texture: 'plain', font: 'arial', paper: 'light_green' },
    // The rejected first GreenFi build, preserved as its own
    // library entry per Princewill's disposition (2026-11-04):
    // the name "GreenFi" belongs to the exact rebuild, so the
    // mint/linen build lives on under this id with its original
    // design tokens (linen weave, mint identity colours, strong
    // frame colour in bankTemplates/design maps).
    GREENFI_MINT: { texture: 'linen', font: 'arial', paper: 'light_green' },
    // ANB EXACT rebuild (2026-11-04): the specimen face is the
    // Deluxe scenic — sky + clouds over a golden wheat field,
    // drawn whole by the scenic_field background (the field's
    // grain is the yellow pattern). The old entry's cloud_band
    // painted only the blue sky, mid-face, and the yellow field
    // never existed — the owner's named defect.
    ANB: { texture: 'scenic_field', font: 'arial', paper: 'cream' },
    // The rejected first ANB build, preserved as its own library
    // entry (the GREENFI_MINT pattern, owner's disposition): the
    // name "ANB Bank" belongs to the exact rebuild, so the
    // cloud-band build lives on under this id with its original
    // design tokens (cloud_band over cream, DOUBLE border, navy
    // identity colours in bankTemplates/design maps).
    ANB_SKY: { texture: 'cloud_band', font: 'arial', paper: 'cream' },
    PNC: { texture: 'blue_wave', font: 'arial', paper: 'light_blue' },
    TD: { texture: 'cotton', font: 'arial', paper: 'light_green' },
    CAPITAL_ONE: { texture: 'prismatic', font: 'helvetica', paper: 'white' },
    US_BANK: { texture: 'laser_security', font: 'helvetica', paper: 'white' },
    HUNTINGTON: { texture: 'parchment', font: 'georgia', paper: 'white' },
    FIFTH_THIRD: { texture: 'multi_tone', font: 'arial', paper: 'white' },
    KEYBANK: { texture: 'circuit_board', font: 'helvetica', paper: 'white' },
    CITIZENS: { texture: 'micro_embossed', font: 'arial', paper: 'light_green' },
    ALLY: { texture: 'digital_hive', font: 'helvetica', paper: 'white' },
    DISCOVER: { texture: 'nano_optical', font: 'arial', paper: 'white' },
    // ---- Regional banks ----------------------------------------------
    REGIONS: { texture: 'royal_damask', font: 'garamond', paper: 'white' },
    TRUIST: { texture: 'interference', font: 'helvetica', paper: 'white' },
    BMO: { texture: 'security', font: 'arial', paper: 'white' },
    ZIONS: { texture: 'rough', font: 'helvetica', paper: 'white' },
    COMERICA: { texture: 'toner_adhesion', font: 'arial', paper: 'white' },
    FIRST_HORIZON: { texture: 'fiber_optic', font: 'georgia', paper: 'white' },
    M_AND_T: { texture: 'particles', font: 'times', paper: 'white' },
    // ---- Credit unions -------------------------------------------------
    NAVY_FCU: { texture: 'fluorescent_fibers', font: 'arial', paper: 'white' },
    PENFED: { texture: 'linen', font: 'arial', paper: 'cream' },
    BECU: { texture: 'digital_hive', font: 'arial', paper: 'white' },
    ALLIANT: { texture: 'particles', font: 'arial', paper: 'white' },
    GOLDEN1: { texture: 'marble', font: 'arial', paper: 'ivory' },
    // ---- Specialty -----------------------------------------------------
    CASHIER: { texture: 'guilloche_bg', font: 'georgia', paper: 'light_green' },
    PAYROLL: { texture: 'plain', font: 'courier', paper: 'white' },
    ECHECK: { texture: 'digital_hive', font: 'arial', paper: 'white' },
    MONEY_ORDER: { texture: 'parchment', font: 'helvetica', paper: 'ivory' },
    TRAVELERS_CHECK: { texture: 'parchment', font: 'times', paper: 'antique' },
    COUNTER_CHECK: { texture: 'plain', font: 'arial', paper: 'white' },
    CERTIFIED: { texture: 'marble', font: 'times', paper: 'antique' },
    CONTRACTOR: { texture: 'rough', font: 'arial', paper: 'white' },
    VOUCHER: { texture: 'linen', font: 'courier', paper: 'white' },
    INDUSTRIAL_PAYROLL: { texture: 'toner_adhesion', font: 'helvetica', paper: 'white' },
    AMEX_CENTURION: { texture: 'guilloche_bg', font: 'georgia', paper: 'white' },
    // ---- Classic / generic ---------------------------------------------
    GENERIC: { texture: 'guilloche_bg', font: 'arial', paper: 'white' },
    CLASSIC_BLUE: { texture: 'linen', font: 'times', paper: 'light_blue' },
    CLASSIC_GREEN: { texture: 'linen', font: 'times', paper: 'light_green' },
    CLASSIC_MAROON: { texture: 'guilloche_bg', font: 'georgia', paper: 'light_pink' },
    PARCHMENT: { texture: 'parchment', font: 'garamond', paper: 'antique' },
    MARBLE: { texture: 'marble', font: 'georgia', paper: 'white' },
    // ---- International ---------------------------------------------------
    CANADIAN: { texture: 'linen', font: 'arial', paper: 'white' },
    UK_STYLE: { texture: 'guilloche_bg', font: 'times', paper: 'ivory' },
    // ---- Owner specialty -------------------------------------------------
    KAREN_ART_STUDIO_PAYROLL: { texture: 'linen', font: 'arial', paper: 'white', opacity: 0.4 },
    // ---- Premium (non-bank) designs --------------------------------------
    classic_elegant: { texture: 'guilloche_bg', font: 'garamond', paper: 'antique', opacity: 0.9 },
    executive_gold: { texture: 'guilloche_bg', font: 'georgia', paper: 'antique', opacity: 0.9 },
    royal_blue: { texture: 'security', font: 'helvetica', paper: 'light_blue', opacity: 0.8 },
    corporate_grey: { texture: 'cotton', font: 'arial', paper: 'white' },
    emerald_prestige: { texture: 'marble', font: 'georgia', paper: 'light_green', opacity: 0.8 },
    burgundy_classic: { texture: 'royal_damask', font: 'garamond', paper: 'peach' },
    midnight_premium: { texture: 'interference', font: 'helvetica', paper: 'lavender' },
    bronze_heritage: { texture: 'parchment', font: 'georgia', paper: 'antique' },
    ultra_obsidian: { texture: 'geometric_diamond', font: 'helvetica', paper: 'white', opacity: 0.85 },
    ultra_sapphire: { texture: 'geometric_diamond', font: 'helvetica', paper: 'light_blue', opacity: 0.85 },
    ultra_amethyst: { texture: 'geometric_diamond', font: 'garamond', paper: 'light_pink', opacity: 0.85 },
    ultra_crimson: { texture: 'geometric_diamond', font: 'arial', paper: 'white', opacity: 0.85 },
    ultra_diamond: { texture: 'geometric_diamond', font: 'courier', paper: 'white', opacity: 0.85 },
    corporate_safety: { texture: 'blue_wave', font: 'arial', paper: 'light_blue' },
    us_treasury: { texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' },
    chime_pro: { texture: 'digital_hive', font: 'helvetica', paper: 'white' },
    credit_union_classic: { texture: 'linen', font: 'times', paper: 'white' },
    varo_style: { texture: 'circuit_board', font: 'helvetica', paper: 'lavender' },
    prepaid_gold: { texture: 'particles', font: 'arial', paper: 'ivory' }
};

export interface StylePreset {
    border: string;
    texture: string;
    font: string;
    paper: string;
}

/**
 * Effective design for the check-layout styles. Before this map, the
 * style buttons only set the style id and Personal / Business /
 * Counter / Money Order rendered the identical check (same layout,
 * same default BRAIDED border, same texture and font). Each style now
 * also applies its own border / texture / font / paper — all real
 * studio parameters. A border of 'NONE' is deliberate for echeck,
 * counter, bofa_classic and bofa_v2: those styles carry their own
 * canvas-drawn frame logic in the renderer (borderless eCheck,
 * modern minimal counter frame, BofA classic double rule, BofA v2
 * braided ribbon), which only runs when the overlay border is NONE.
 *
 * payroll_karen is intentionally absent: it is the owner's client
 * design with its own full-page layout, and its look is set by its
 * template branch, never overridden here. Its style button is not a
 * no-op, though — it routes through that template branch instead
 * (see STYLE_TEMPLATE_ROUTES below). The three specimen styles
 * (greenfi, anb, usaa) are absent for the same reason: they route
 * through their template branches, which set their own design
 * tokens from TEMPLATE_DESIGN.
 */
export const STYLE_PRESETS: Record<string, StylePreset> = {
    personal: { border: 'DOUBLE', texture: 'linen', font: 'times', paper: 'cream' },
    business: { border: 'COMMERCIAL', texture: 'security', font: 'helvetica', paper: 'white' },
    payroll: { border: 'SOLID', texture: 'cotton', font: 'arial', paper: 'white' },
    payroll_classic: { border: 'DOUBLE', texture: 'parchment', font: 'garamond', paper: 'antique' },
    cashier: { border: 'TREASURY', texture: 'guilloche_bg', font: 'georgia', paper: 'light_green' },
    echeck: { border: 'NONE', texture: 'digital_hive', font: 'arial', paper: 'white' },
    money_order: { border: 'ORNATE', texture: 'parchment', font: 'georgia', paper: 'ivory' },
    certified: { border: 'FILIGREE', texture: 'marble', font: 'times', paper: 'antique' },
    counter: { border: 'NONE', texture: 'plain', font: 'arial', paper: 'white' },
    bofa_classic: { border: 'NONE', texture: 'cloud_marble', font: 'courier', paper: 'light_blue' },
    bofa_v2: { border: 'NONE', texture: 'blue_wave', font: 'arial', paper: 'white' },
    us_treasury: { border: 'TREASURY', texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' }
};

/**
 * Style buttons that ARE a template (2026-11-03 Karen-button fix).
 *
 * Every other layout style is a generic preset above: the button sets
 * the style id plus border / texture / font / paper and the renderer
 * keys its furniture off the style id alone. payroll_karen is
 * different — the design's identity (account holder "Karen Art
 * Studio", bank "My Bank", payee, derived fractional routing, design
 * parameters) lives ONLY in the KAREN_ART_STUDIO_PAYROLL branch of
 * applyBankTemplate. Setting the style id alone therefore left the
 * previously selected design's bank, colours and data sitting on the
 * Karen layout, and from any other design the button read as dead:
 * the picker produced the Karen check, the button did not.
 *
 * The three 2026-11-04 specimen designs (GreenFi, ANB Bank, USAA)
 * are the same class: their identity furniture (wordmark / bank
 * block / Photo Safe mark, per-design security set, seal colour,
 * header suppression, MICR-caption treatment) lives only in their
 * applyBankTemplate branches, keyed off selectedBank. Their layout
 * styles therefore route through the template branch too, exactly
 * like Karen's — a style button that set only the id would print a
 * generic personal face wearing the design's name.
 *
 * applyStylePreset routes these style ids through applyBankTemplate,
 * so the button produces exactly the state the template picker
 * produces (selectedBank + style + data). styleButtons.test.ts pins
 * that every style id has a non-no-op apply path — a preset here or a
 * route there, never neither, never both — and that each route
 * round-trips: resolveTemplateStyle(route) is the style id itself.
 */
export const STYLE_TEMPLATE_ROUTES: Record<string, string> = {
    payroll_karen: 'KAREN_ART_STUDIO_PAYROLL',
    greenfi: 'GREENFI',
    anb: 'ANB',
    usaa: 'USAA'
};

/**
 * The check-layout style each template brings with it (2026-11-03
 * front-fix). Why this map exists: applyBankTemplate used to set only
 * the template id + design parameters and never the layout style, so
 * the previously selected style's face furniture leaked into every
 * template applied after it — pick Money Order, then Chase, and the
 * "MONEY ORDER / PERSONAL MONEY ORDER" heading stayed on the Chase
 * face; pick BofA Classic, then a premium design, and the premium
 * check inherited the BofA MICR variant (see micrVariantFor). A
 * template must yield its own complete face, so applying one now sets
 * the layout style from this map: type templates get their own layout
 * (money order, eCheck, cashier's, treasury, the two BofA layouts,
 * Karen payroll), the three specimen bank designs get their own
 * layouts too (2026-11-04 layout registration — until then they rode
 * the personal layout, so the layout grid had no GreenFi / ANB /
 * USAA entry at all), bank-issued business/retail designs get the
 * business layout, and member/classic/personal designs get the
 * personal layout (the premium split mirrors backVariants' business/
 * mobile families). micrVariant.test.ts enforces coverage for every
 * template id.
 */
export const TEMPLATE_STYLE: Record<string, string> = {
    // ---- Type templates: their own layout ------------------------------
    BOFA_CLASSIC: 'bofa_classic',
    BOFA_BUSINESS_V2: 'bofa_v2',
    US_TREASURY_OFFICIAL: 'us_treasury',
    us_treasury: 'us_treasury',
    KAREN_ART_STUDIO_PAYROLL: 'payroll_karen',
    MONEY_ORDER: 'money_order',
    ECHECK: 'echeck',
    CASHIER: 'cashier',
    CERTIFIED: 'certified',
    COUNTER_CHECK: 'counter',
    PAYROLL: 'payroll',
    INDUSTRIAL_PAYROLL: 'payroll',
    // ---- Bank-issued business / retail-major designs -------------------
    BOFA: 'business',
    CHASE: 'business',
    CHASE_PRIVATE: 'business',
    WELLS: 'business',
    CITI: 'business',
    PNC: 'business',
    TD: 'business',
    CAPITAL_ONE: 'business',
    US_BANK: 'business',
    HUNTINGTON: 'business',
    FIFTH_THIRD: 'business',
    KEYBANK: 'business',
    CITIZENS: 'business',
    ALLY: 'business',
    DISCOVER: 'business',
    REGIONS: 'business',
    TRUIST: 'business',
    BMO: 'business',
    ZIONS: 'business',
    COMERICA: 'business',
    FIRST_HORIZON: 'business',
    M_AND_T: 'business',
    AMEX_CENTURION: 'business',
    CONTRACTOR: 'business',
    VOUCHER: 'business',
    // ---- Member / classic / personal designs ---------------------------
    USAA: 'usaa',
    GREENFI: 'greenfi',
    GREENFI_MINT: 'personal',
    ANB: 'anb',
    ANB_SKY: 'anb',
    NAVY_FCU: 'personal',
    PENFED: 'personal',
    BECU: 'personal',
    ALLIANT: 'personal',
    GOLDEN1: 'personal',
    GENERIC: 'personal',
    CLASSIC_BLUE: 'personal',
    CLASSIC_GREEN: 'personal',
    CLASSIC_MAROON: 'personal',
    PARCHMENT: 'personal',
    MARBLE: 'personal',
    CANADIAN: 'personal',
    UK_STYLE: 'personal',
    TRAVELERS_CHECK: 'personal',
    // ---- Premium designs (split mirrors backVariants' families) --------
    classic_elegant: 'business',
    executive_gold: 'business',
    royal_blue: 'business',
    corporate_grey: 'business',
    emerald_prestige: 'business',
    burgundy_classic: 'business',
    midnight_premium: 'business',
    bronze_heritage: 'business',
    ultra_obsidian: 'business',
    ultra_sapphire: 'business',
    ultra_amethyst: 'business',
    ultra_crimson: 'business',
    ultra_diamond: 'business',
    corporate_safety: 'business',
    prepaid_gold: 'business',
    chime_pro: 'personal',
    credit_union_classic: 'personal',
    varo_style: 'personal'
};

/** The layout style a template application selects. Unknown future
 *  ids fall back to the business layout rather than inheriting
 *  whatever style happened to be active (the leak this map fixes). */
/**
 * Per-template border treatment (2026-11-04 specimen designs).
 * Most templates keep whatever border the style/user has chosen;
 * a design whose printed frame IS its identity carries its own:
 * GreenFi's FSC microprint frame and USAA's bold navy double rules
 * (top and bottom edges only). Both styles are drawn by
 * utils/canvasBorder in the renderer's texture pass, so they print
 * and export with the check.
 *
 * ANB's entry is the opposite case (EXACT rebuild, 2026-11-04):
 * the specimen's scenic art bleeds to all four edges — no border
 * rule prints anywhere on the original — so the exact build pins
 * NONE (the border picker's own no-border entry; the owner's
 * GreenFi ruling is the same instinct: "the main one doesn't have
 * that borderline"). The earlier DOUBLE pin recorded the old
 * build's code history, not the specimen; that treatment is
 * preserved under ANB_SKY below, style-only exactly as before.
 */
export const TEMPLATE_BORDER: Record<string, { style: string; color?: string }> = {
    // GreenFi EXACT rebuild: the frame colour is the PALE
    // microprint green of the specimen's writing band — saturated
    // green belongs to the wordmark only.
    GREENFI: { style: 'MICROPRINT_FRAME', color: '#8cae7f' },
    // The preserved first build keeps its strong frame colour.
    GREENFI_MINT: { style: 'MICROPRINT_FRAME', color: '#2f9e44' },
    USAA: { style: 'DOUBLE_RULE_BOLD', color: '#0a2d5c' },
    // ANB EXACT rebuild: no borderline on the original (see the
    // note above). The preserved first build keeps its DOUBLE.
    ANB: { style: 'NONE' },
    ANB_SKY: { style: 'DOUBLE' }
};

export function resolveTemplateStyle(bankId: string): string {
    return TEMPLATE_STYLE[bankId] || 'business';
}

export type MicrVariant = 'standard' | 'bofa';

/**
 * Which MICR composition a design renders. The BofA pattern (routing
 * bracketed by transit symbols, account followed by the on-us symbol,
 * check number bare, no field captions) belongs ONLY to the BofA
 * Classic design — the bofa_classic layout style or the BOFA_CLASSIC
 * template. Everything else, including the BofA Business v2 design,
 * renders the standard composition (on-us-led account, check number
 * closed by on-us, TRANSIT ROUTING NUMBER / ACCOUNT NUMBER captions).
 * Single source for the gate so the renderer and the regression test
 * can never disagree.
 */
export function micrVariantFor(checkStyle: string, selectedBank: string | null | undefined): MicrVariant {
    return (checkStyle === 'bofa_classic' || selectedBank === 'BOFA_CLASSIC') ? 'bofa' : 'standard';
}
